import { useCallback, useMemo, useRef, useState } from 'react'
import { RemoteTableRef } from 'medere-ui-framework'
import { HospitalizationFilterDTO } from '../../dto/hospitalizationSummary/HospitalizationSummaryDTO'
import {
  HospitalizationTableParams,
  isAccessDenied,
  isRequestCanceled,
} from '../../services/HospitalizationSummaryController'

/** Resultado de la última consulta de la grilla: `error` y `denied` no son una grilla vacía. */
export type HospitalizationGridState = 'ok' | 'error' | 'denied'

/**
 * Lo común a las grillas de la pantalla (lista, internados hoy y evoluciones): le da a la tabla los filtros de pantalla en
 * `params` (llegan a `getItems` junto con los de columna y el orden), distingue el error y el «sin permiso» de una grilla
 * vacía y avisa de cada pedido de la tabla (`onLoad`): lo último que pidió es lo que ve el usuario y, por lo tanto, lo que
 * exporta «Exportar». Los filtros y el orden de la tabla quedan en el navegador mientras se cambia de pestaña; la pantalla
 * los borra al abrirse y al cerrarse.
 *
 * @param filters filtros aplicados de la pantalla (período y «Más filtros»).
 * @param load pide una página al backend.
 * @param onLoad avisa de cada pedido de la tabla (para guardarlo como lo último que se vio).
 */
export default function useHospitalizationGrid<TRow>(
  filters: HospitalizationFilterDTO,
  load: (offSet: number, pageSize: number, params: HospitalizationTableParams) => Promise<TRow[]>,
  onLoad: (params: HospitalizationTableParams) => void,
) {
  const tableRef = useRef<RemoteTableRef>(null)
  const [state, setState] = useState<HospitalizationGridState>('ok')

  /** Los filtros de la pantalla llegan a `getItems` junto con los de columna y el orden de la tabla. */
  const params = useMemo(() => ({ filters }), [filters])

  const loadItems = useCallback(async (offSet: number, pageSize: number, tableParams?: HospitalizationTableParams): Promise<TRow[]> => {
    const request = tableParams ?? {}
    onLoad(request)
    try {
      const rows = await load(offSet, pageSize, request)
      setState('ok')
      return rows
    } catch (error: unknown) {
      if (!isRequestCanceled(error)) setState(isAccessDenied(error) ? 'denied' : 'error')
      return []
    }
  }, [load, onLoad])

  const retry = useCallback(() => tableRef.current?.refreshTable?.(), [])

  return { tableRef, params, loadItems, state, retry }
}
